import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  AnnualIncomeContainer,
  AnnualIncomeLabel,
  SliderContainer,
  SliderTrack,
  SliderThumb,
  SliderRange,
  IncomeInput,
  RangeLabels,
  RangeLabel,
} from "./AnnualIncomeSlider.styles";

const AnnualIncomeSlider = ({
  title,
  value,
  onChange,
  name,
  disabled = false,
  options = [], // Add options prop for API ranges
}) => {
  // Define helper functions first (removed unused formatCurrency and parseCurrency)

  // Get range options from API or use default ranges
  const getRangeOptions = () => {
    if (options && options.length > 0) {
      return options.map((option, index) => ({
        value: option.slug || option.value,
        label: option.text || option.label,
        index: index,
      }));
    }
    // Default ranges if no options provided
    return [
      { value: "zero_to_50k_income", label: "$0-$50,000", index: 0 },
      { value: "50k_to_100k_income", label: "$50,001-$100,000", index: 1 },
      { value: "100k_to_250k_income", label: "$100,001-$250,000", index: 2 },
      { value: "250k_to_1m_income", label: "$250,001-$1,000,000", index: 3 },
      { value: "500k_to_1m_income", label: "$500,000-$1 million", index: 4 },
      { value: "over_1m_income", label: "Over $1 million", index: 5 },
    ];
  };

  const rangeOptions = getRangeOptions();

  const getCurrentRangeIndex = () => {
    const currentOption = rangeOptions.find((option) => option.value === value);
    return currentOption ? currentOption.index : 0;
  };

  const getSliderPercentage = () => {
    const currentIndex = getCurrentRangeIndex();
    return rangeOptions.length > 1
      ? (currentIndex / (rangeOptions.length - 1)) * 100
      : 0;
  };

  const getCurrentRangeLabel = () => {
    const currentOption = rangeOptions.find((option) => option.value === value);
    const rangeText = currentOption
      ? currentOption.label
      : rangeOptions[0]?.label || "";
    return rangeText
      ? `My annual income is ${rangeText}`
      : "My annual income is $100K - $250K";
  };

  // State declarations
  const [sliderIndex, setSliderIndex] = useState(getCurrentRangeIndex());
  const [inputValue, setInputValue] = useState(getCurrentRangeLabel());

  useEffect(() => {
    if (value !== undefined) {
      const newIndex = getCurrentRangeIndex();
      setSliderIndex(newIndex);
      setInputValue(getCurrentRangeLabel());
    }
  }, [value, rangeOptions]);

  const handleSliderChange = (event) => {
    const newIndex = parseInt(event.target.value);
    const selectedOption = rangeOptions[newIndex];

    if (selectedOption) {
      setSliderIndex(newIndex);
      setInputValue(selectedOption.label);
      onChange(selectedOption.value);
    }
  };

  const handleInputChange = (event) => {
    const rawValue = event.target.value;
    setInputValue(rawValue);

    // Find matching range option
    const matchingOption = rangeOptions.find((option) =>
      option.label.toLowerCase().includes(rawValue.toLowerCase())
    );

    if (matchingOption) {
      setSliderIndex(matchingOption.index);
      onChange(matchingOption.value);
    }
  };

  const handleInputBlur = () => {
    // Reset to current selected range on blur
    setInputValue(getCurrentRangeLabel());
  };

  return (
    <AnnualIncomeContainer>
      {title && <AnnualIncomeLabel>{title}</AnnualIncomeLabel>}

      <SliderContainer>
        <SliderTrack>
          <SliderRange $percentage={getSliderPercentage()} />
          <SliderThumb
            type="range"
            min={0}
            max={rangeOptions.length - 1}
            step={1}
            value={sliderIndex}
            onChange={handleSliderChange}
            disabled={disabled}
            name={name}
          />
        </SliderTrack>

        <RangeLabels>
          <RangeLabel>{rangeOptions[0]?.label || "$0"}</RangeLabel>
          <RangeLabel>
            {rangeOptions[rangeOptions.length - 1]?.label || "Over $1M"}
          </RangeLabel>
        </RangeLabels>
      </SliderContainer>

      <IncomeInput
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder="My annual income is $100K - $250K"
        disabled={disabled}
        readOnly
      />
    </AnnualIncomeContainer>
  );
};

AnnualIncomeSlider.propTypes = {
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array,
};

export { AnnualIncomeSlider };
